import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getCollection, getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { Product } from '@chancenwerk/_cms/products/entities/Product.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { DateTime } from 'luxon';
import { Observable, catchError, from, of, switchMap } from 'rxjs';
import { ChancenwerkUser } from '../../master-data-management/users/entities/ChancenwerkUser.js';
import { ChancenEventlog } from './ChancenEventlog.js';

@Entity()
export class GuestDayEventlogByMonth extends AdornisEntity {
  static override _class = 'GuestDayEventlogByMonth';

  public static override resolveID(entity: any): string {
    return A.getGloballyUniqueID();
  }

  @DataLoadedField(type => KPF, ({ kpfID }) => kpfID)
  kpf: Maybe<KPF>;

  @Field(type => String)
  kpfID: Maybe<string>;

  @Field(type => DateTime)
  date: Maybe<DateTime>;

  @Field(type => Int)
  count: Maybe<number>;

  @Field(type => [GuestDayEventlog])
  guestDayEventlogs: Maybe<GuestDayEventlog[]>;
}

@Entity()
export class GuestDayEventlog extends ChancenEventlog {
  static override _class = 'GuestDayEventlog';

  constructor(doc: PartialEntityData<GuestDayEventlog>) {
    super(doc);
  }

  @Field(type => String)
  creator: Maybe<string>;

  @Field(type => String)
  kpfID: Maybe<string>;

  @validate(nonOptional())
  @Field(type => DateTime)
  date: Maybe<DateTime>;

  @DataLoadedField(type => ChancenwerkUser, ({ userID }) => userID)
  user: Maybe<ChancenwerkUser>;

  @DataLoadedField(type => KPF<Product>, ({ kpfID }) => kpfID)
  kpf: Maybe<KPF<Product>>;

  @Query(type => [GuestDayEventlog])
  public static getGuestDayEventlogByKpf(@Arg('kpfID', type => String) kpfID: string) {
    return async (gqlFields: BaseQLSelectionSet<GuestDayEventlog>) => {
      const col = await getCollection<GuestDayEventlog>(GuestDayEventlog._class);

      return col.find({ kpfID }).toArray();
    };
  }

  @Subscription(type => [GuestDayEventlog])
  public static subscribeGuestDayEventlogByKpf(
    @Arg('kpfID', type => String) kpfID: string,
    @Arg('dateFrom', type => DateTime) dateFrom?: DateTime,
    @Arg('dateTo', type => DateTime) dateTo?: DateTime,
  ) {
    return (gqlFields: BaseQLSelectionSet<GuestDayEventlog>): Observable<GuestDayEventlog[]> => {
      const selector = {
        kpfID,
        ...(dateFrom && dateTo ? { date: { $gte: dateFrom.toJSDate(), $lte: dateTo.toJSDate() } } : {}),
      };
      const handle = getCollectionHandle<GuestDayEventlog>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          return of([] as GuestDayEventlog[]);
        }),
      );
    };
  }

  @Subscription(type => [GuestDayEventlogByMonth])
  public static subscribeGuestDayEventlogByMonth(
    @Arg('kpfID', type => [String]) kpfIDs: string[],
    @Arg('dateFrom', type => DateTime) dateFrom: DateTime,
    @Arg('dateTo', type => DateTime) dateTo: DateTime,
  ) {
    return (gqlFields: BaseQLSelectionSet<GuestDayEventlogByMonth>) => {
      const selector = {
        date: { $gte: dateFrom.toJSDate(), $lte: dateTo.toJSDate() },
        ...(kpfIDs.length > 0 ? { kpfID: { $in: kpfIDs } } : {}),
      };
      const handle = getCollectionHandle<GuestDayEventlog>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        switchMap(async resultIDs => {
          const col = await getRawCollection<GuestDayEventlog>(this._collectionName);
          const res = await col
            .aggregate([
              { $match: { _id: { $in: resultIDs.map(x => x._id) } } },
              {
                $group: {
                  _id: { kpfID: '$kpfID', date: { $dateToString: { format: '%Y-%m', date: '$date' } } },
                  count: { $sum: 1 },
                  guestDayEventlogs: { $push: '$$ROOT' },
                },
              },
            ])
            .toArray();
          return res.map(x => {
            return new GuestDayEventlogByMonth({
              kpfID: x._id.kpfID,
              date: DateTime.fromFormat(x._id.date as unknown as string, 'yyyy-MM').toJSDate(),
              count: x.count,
              guestDayEventlogs: x.guestDayEventlogs.map(e => new GuestDayEventlog(e)),
            });
          });
        }),
        catchError(e => {
          return of([] as GuestDayEventlogByMonth[]);
        }),
      );
    };
  }
}

if (A.isServer) {
  const attendanceEventLogs = getRawCollection<any>(GuestDayEventlog._collectionName);

  // Create DB Indices
  void attendanceEventLogs.then(async db => {
    void db.createIndex({ kpfID: 1 });
  });
}
